import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import ReactFlow, { Background, Controls, MiniMap, MarkerType, useNodesState, useEdgesState } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Network, Loader2, AlertTriangle, ArrowRight, ArrowLeft, Eye,
  FileText, CheckSquare, FileBarChart, Share2, Mic, Map, RotateCcw, Maximize2,
  Download, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  article: FileText, task: CheckSquare, report: FileBarChart,
  knowledge_transfer: Share2, voice_journal: Mic, knowledge_map: Map,
};

const KnowledgeDepsPage = () => {
  const { token } = useAuth();
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impact, setImpact] = useState(null);
  const [impactOpen, setImpactOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => { fetchGraph(); }, []);

  const fetchGraph = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/knowledge-dependencies/graph`, { headers: { Authorization: `Bearer ${token}` } });
      setGraphData(res.data);
      buildFlowGraph(res.data);
    } catch { toast.error('Failed to load dependency graph'); }
    finally { setLoading(false); }
  };

  const buildFlowGraph = (data) => {
    if (!data?.nodes) return;

    // Filter to only nodes with connections for cleaner graph, plus top isolated ones
    const connected = new Set();
    data.edges.forEach(e => { connected.add(e.source); connected.add(e.target); });
    const connectedNodes = data.nodes.filter(n => connected.has(n.id));
    const isolatedNodes = data.nodes.filter(n => !connected.has(n.id)).slice(0, 10);
    const displayNodes = [...connectedNodes, ...isolatedNodes];

    // Position nodes in a force-like layout
    const cols = Math.ceil(Math.sqrt(displayNodes.length));
    const flowNodes = displayNodes.map((n, i) => ({
      id: n.id,
      data: { label: n.label, type: n.type, color: n.color, deps: n.dependencies, inbound: n.inbound, outbound: n.outbound },
      position: { x: (i % cols) * 250 + Math.random() * 50, y: Math.floor(i / cols) * 150 + Math.random() * 30 },
      style: {
        background: n.color,
        color: 'white',
        border: `2px solid ${n.color}`,
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '11px',
        fontWeight: 500,
        width: 180,
        cursor: 'pointer',
        boxShadow: n.dependencies > 3 ? '0 0 12px rgba(0,47,167,0.3)' : 'none',
      },
    }));

    const edgeColors = { related: '#002FA7', shared_tag: '#34C759', mentions: '#FF9500' };
    const flowEdges = data.edges.slice(0, 200).map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label?.length < 15 ? e.label : '',
      type: 'smoothstep',
      animated: e.type === 'mentions',
      style: { stroke: edgeColors[e.type] || '#9CA3AF', strokeWidth: 1.5, opacity: 0.6 },
      markerEnd: { type: MarkerType.ArrowClosed, color: edgeColors[e.type] || '#9CA3AF' },
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);
  };

  const onNodeClick = useCallback(async (_, node) => {
    setSelectedNode(node);
    try {
      const res = await axios.get(`${API}/knowledge-dependencies/${node.id}/impact`, { headers: { Authorization: `Bearer ${token}` } });
      setImpact(res.data);
      setImpactOpen(true);
    } catch { toast.error('Failed to load impact analysis'); }
  }, [token]);

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'bg-[#FF3B30] text-white';
      case 'high': return 'bg-[#FF9500] text-white';
      case 'medium': return 'bg-[#FFCC00] text-[#0A0A0A]';
      default: return 'bg-[#34C759] text-white';
    }
  };

  if (loading) return <div className="flex items-center justify-center h-[80vh]"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;

  return (
    <div data-testid="knowledge-deps-page" className="h-[calc(100vh-80px)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Knowledge Dependencies</h1>
          <p className="text-[#4B5563] text-sm">Visualize how knowledge assets reference each other</p>
        </div>
        <div className="flex gap-2 items-center">
          {graphData && (
            <div className="flex gap-3 text-xs text-[#9CA3AF] mr-4">
              <span><strong>{graphData.stats?.total_nodes}</strong> assets</span>
              <span><strong>{graphData.stats?.total_edges}</strong> connections</span>
              <span><strong>{graphData.stats?.isolated_nodes}</strong> isolated</span>
            </div>
          )}
          <Button size="sm" variant="outline" onClick={async () => {
            try { const res = await axios.get(`${API}/knowledge-dependencies/export-pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); const a = document.createElement('a'); a.href = url; a.download = 'dependencies-report.pdf'; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url); } catch { toast.error('Download failed'); }
          }} data-testid="export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
          <Button size="sm" variant="outline" onClick={async () => {
            const emails = prompt('Enter email addresses (comma-separated):'); if (!emails) return;
            const message = prompt('Add a personal message (optional):') || '';
            try { const res = await axios.post(`${API}/knowledge-dependencies/share`, { to_emails: emails.split(',').map(e => e.trim()).filter(Boolean), message }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(res.data.message); } catch (e) { toast.error(e.response?.data?.detail || 'Share failed'); }
          }} data-testid="share-email-btn"><Mail className="w-3.5 h-3.5 mr-1" />Share</Button>
          <Button variant="outline" size="sm" onClick={fetchGraph} data-testid="refresh-graph-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
        </div>
      </div>

      {/* Legend + Stats Bar */}
      <div className="flex items-center gap-4 mb-3 text-xs">
        <span className="text-[#9CA3AF]">Edge Types:</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-[#002FA7] inline-block" />Related</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-[#34C759] inline-block" />Shared Tag</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-[#FF9500] inline-block" />Mentions</span>
        <span className="text-[#9CA3AF] ml-4">Node Types:</span>
        {[{l:'Article',c:'#002FA7'},{l:'Task',c:'#34C759'},{l:'Report',c:'#FF9500'},{l:'Transfer',c:'#AF52DE'},{l:'Voice',c:'#FF3B30'},{l:'Map',c:'#007AFF'}].map(t => (
          <span key={t.l} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{background:t.c}} />{t.l}</span>
        ))}
      </div>

      {/* Graph */}
      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden" style={{ height: 'calc(100% - 120px)' }} data-testid="dependency-graph">
        {graphData && nodes.length > 0 ? (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            fitView
            minZoom={0.1}
            maxZoom={2}
            attributionPosition="bottom-left"
          >
            <Background color="#E5E7EB" gap={20} />
            <Controls />
            <MiniMap nodeColor={(n) => n.style?.background || '#9CA3AF'} maskColor="rgba(255,255,255,0.8)" />
          </ReactFlow>
        ) : (
          <div className="flex items-center justify-center h-full text-[#9CA3AF]">
            <div className="text-center">
              <Network className="w-16 h-16 mx-auto mb-4 text-[#E5E7EB]" />
              <p>No dependency data. Add more knowledge assets with tags and references.</p>
            </div>
          </div>
        )}
      </div>

      {/* Impact Analysis Dialog */}
      <Dialog open={impactOpen} onOpenChange={setImpactOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="impact-dialog">
          <DialogHeader><DialogTitle>Impact Analysis</DialogTitle></DialogHeader>
          {impact && (
            <div className="space-y-4">
              {/* Asset Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{impact.asset?.label}</h3>
                  <p className="text-sm text-[#9CA3AF] capitalize">{impact.asset?.type?.replace('_', ' ')}</p>
                </div>
                <div className="flex gap-2">
                  <Badge className={getRiskColor(impact.risk)}>{impact.risk} impact</Badge>
                  <div className="text-center px-3 py-1 bg-[#F3F4F6] rounded-lg">
                    <p className="text-lg font-bold">{impact.impact_score}</p>
                    <p className="text-[10px] text-[#9CA3AF]">Score</p>
                  </div>
                </div>
              </div>

              {/* Impact Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-[#F3F4F6] rounded-lg">
                  <p className="text-xl font-bold text-[#002FA7]">{impact.direct_count}</p>
                  <p className="text-xs text-[#9CA3AF]">Direct Dependencies</p>
                </div>
                <div className="text-center p-3 bg-[#F3F4F6] rounded-lg">
                  <p className="text-xl font-bold text-[#FF9500]">{impact.second_degree_count}</p>
                  <p className="text-xs text-[#9CA3AF]">2nd Degree</p>
                </div>
                <div className="text-center p-3 bg-[#F3F4F6] rounded-lg">
                  <p className="text-xl font-bold">{impact.total_impacted}</p>
                  <p className="text-xs text-[#9CA3AF]">Total Impacted</p>
                </div>
              </div>

              {/* Recommendation */}
              <div className={`p-3 rounded-lg border text-sm ${impact.risk === 'critical' ? 'bg-[#FFF5F5] border-[#FED7D7] text-[#FF3B30]' : impact.risk === 'high' ? 'bg-[#FFF8F0] border-[#FED7AA] text-[#FF9500]' : 'bg-[#F0FFF4] border-[#C6F6D5] text-[#34C759]'}`} data-testid="impact-recommendation">
                <AlertTriangle className="w-4 h-4 inline-block mr-2" />{impact.recommendation}
              </div>

              {/* Direct Dependencies */}
              {impact.direct_dependencies?.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">Direct Dependencies ({impact.direct_count})</h4>
                  <div className="space-y-1">
                    {impact.direct_dependencies.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded hover:bg-[#F9FAFB] text-sm" data-testid={`dep-${d.id}`}>
                        <div className="flex items-center gap-2">
                          {d.direction === 'outbound' ? <ArrowRight className="w-3 h-3 text-[#002FA7]" /> : <ArrowLeft className="w-3 h-3 text-[#34C759]" />}
                          <span className="font-medium">{d.label}</span>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="secondary" className="text-[10px]">{d.relation.replace('_', ' ')}</Badge>
                          <Badge variant="secondary" className="text-[10px] capitalize">{d.type?.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 2nd Degree */}
              {impact.second_degree?.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Ripple Effect — 2nd Degree ({impact.second_degree_count})</h4>
                  <div className="flex flex-wrap gap-1">
                    {impact.second_degree.map((d, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{d.label}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeDepsPage;
